import {Component, inject, Injector, OnInit, signal} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../../basic-material-dialog/basic-material-dialog.directive";
import {Translations} from "@translations";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {AddMembersToSimGroupRequest, CreateGroupRequest, GroupColor, SimGroup, SimGroupType} from "@models";
import {SimGroupingService} from "@services";
import {forkJoin, from, mergeMap} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

export interface DialogData_CreateGroupInput extends MatDialogDataBaseInput {
	type: SimGroupType;
	parentId?: string | null;
	editGroup?: SimGroup | null;
}

export interface DialogData_CreateGroupOutput extends MatDialogDataBaseOutput {
	group: SimGroup;
}

@UntilDestroy()
@Component({
	selector: "sim-create-group-dialog",
	templateUrl: "./create-group-dialog.component.html",
	styleUrl: "./create-group-dialog.component.scss",
	standalone: false,
})
export class CreateGroupDialogComponent
	extends BasicMaterialDialog<CreateGroupDialogComponent, DialogData_CreateGroupInput, DialogData_CreateGroupOutput>
	implements OnInit
{
	// Properties
	public busy = signal<boolean>(false);
	public isSubgroup = signal<boolean>(false);
	public editGroup = signal<SimGroup | null>(null);

	// Form
	public form = new FormGroup({
		name: new FormControl<string>("", [Validators.required]),
		color: new FormControl<GroupColor>(GroupColor.One, [Validators.required]),
	});

	// Readonly
	public readonly T = Translations;

	// Injections
	private _groupService = inject(SimGroupingService);

	constructor(_injector: Injector) {
		super(_injector);
	}

	public ngOnInit() {
		super.ngOnInit();
		this.isSubgroup.set(!!this.data.parentId);
		if (this.data.editGroup) {
			this.form.setValue({name: this.data.editGroup.name, color: this.data.editGroup.color}, {emitEvent: false});
			this.editGroup.set(this.data.editGroup);
		}
	}

	protected doSubmit(): void {
		// Validate
		if (this.form.invalid || this.busy()) {
			return;
		}

		this.busy.set(true);

		if (this.editGroup()) {
			this._submitEdit();
		} else {
			this._submitCreate();
		}
	}

	private _submitEdit(): void {
		const editGroup = this.editGroup();
		const formValue = this.form.getRawValue();
		if (!editGroup?.id || !formValue.name || formValue.color === null) {
			this.busy.set(false);
			return;
		}

		forkJoin([
			from(this._groupService.updateNameOfSimGroup(editGroup.id, formValue.name)),
			from(this._groupService.updateColorOfSimGroup(editGroup.id, formValue.color)),
		]).subscribe(([{name}, {color}]) => {
			this.close({action: "ok", group: {...this.data.editGroup, name, color} as SimGroup});
		});
	}

	private _submitCreate(): void {
		this._groupService
			.createSimGroup({
				name: this.form.value.name,
				color: this.form.value.color,
				type: this.data.type,
				parentGroupId: this.data.parentId,
			} as CreateGroupRequest)
			.then((group) => {
				this.close({action: "ok", group});
			});
	}
}
